/**
 * ConfirmDeleteModal Component
 *
 * Confirmation dialog for destructive delete/archive actions.
 * Built on top of {@link ConfirmationModal} with pre-configured danger variant
 * and optional warning slot.
 *
 * Pass `confirmationPhrase` for a deletion that is worth a deliberate pause — a bulk delete,
 * say: the confirm button stays disabled until the phrase is typed back exactly.
 *
 * @example
 * ```tsx
 * <ConfirmDeleteModal
 *   open={showConfirm}
 *   itemName="Supplier ABC"
 *   title="Archive supplier"
 *   message="Are you sure you want to archive this supplier?"
 *   confirmLabel="Archive"
 *   cancelLabel="Cancel"
 *   warning="Archived suppliers won't be available for new documents."
 *   onConfirm={handleDelete}
 *   onClose={() => setShowConfirm(false)}
 * />
 * ```
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { type TestIdProps } from '../../utils';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { Input } from '../../components/inputs';
import { InputWrapper } from '../../components/InputWrapper';

export interface ConfirmDeleteModalProps extends TestIdProps {
  /** Whether the modal is visible */
  open: boolean;
  /** Modal title */
  title: string;
  /** Descriptive message — can contain the item name via interpolation */
  message: React.ReactNode;
  /** Label for the confirm (delete/archive) button */
  confirmLabel: string;
  /** Label for the cancel button */
  cancelLabel: string;
  /** Optional warning message displayed between header and footer */
  warning?: React.ReactNode;
  /**
   * When set, confirming requires typing this phrase back exactly — for deletions whose blast
   * radius is larger than a single row.
   */
  confirmationPhrase?: string;
  /** Label for the confirmation input; shown only with `confirmationPhrase`. */
  confirmationLabel?: string;
  /** Whether the action is in progress */
  loading?: boolean;
  /** Called when the user confirms the action */
  onConfirm: () => void;
  /** Called when the modal is dismissed */
  onClose: () => void;
  /** Additional CSS classes */
  className?: string;
}

export const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  warning,
  confirmationPhrase,
  confirmationLabel,
  loading = false,
  onConfirm,
  onClose,
  className,
  testId,
  id
}) => {
  const [typed, setTyped] = useState('');

  // Every opening starts from an empty field — a phrase typed last time must not carry over.
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const phraseMismatch = !!confirmationPhrase && typed.trim() !== confirmationPhrase;

  return (
    <ConfirmationModal
      open={open}
      title={title}
      message={message}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      variant='danger'
      loading={loading}
      warning={warning}
      confirmDisabled={phraseMismatch}
      body={
        confirmationPhrase ? (
          <InputWrapper label={confirmationLabel} htmlFor='confirm-delete-phrase'>
            <Input
              id='confirm-delete-phrase'
              type='text'
              autoComplete='off'
              value={typed}
              onChange={e => setTyped(e.target.value)}
              placeholder={confirmationPhrase}
            />
          </InputWrapper>
        ) : undefined
      }
      onConfirm={onConfirm}
      onClose={onClose}
      className={className}
      testId={testId}
      id={id}
    />
  );
};

ConfirmDeleteModal.displayName = 'ConfirmDeleteModal';
